import { useEffect, useRef, useState } from "react";
import { useUser } from "@clerk/react";
import { Save, Trash2Icon, UploadIcon, User2Icon } from "lucide-react";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Frame,
  FrameHeader,
  FrameTitle,
  FramePanel,
  FrameFooter,
} from "@/components/ui/frame";
import { toastClerkError } from "@/lib/clerk-errors";
import { ConnectedAccountsFrame } from "./ConnectedAccountsFrame";
import { ActiveDevicesFrame, EmailAddressesFrame } from "./SignInFrames";

// What Clerk accepts for a profile photo.
const AVATAR_TYPES = "image/png,image/jpeg,image/gif,image/webp";
const AVATAR_MAX_BYTES = 10 * 1024 * 1024;

/**
 * Settings › Personal: your Clerk profile, the accounts connected to it
 * (mailbox, Dub, Gmail labeling) and how you sign in. Your writing style
 * lives on Organization, next to everyone else's.
 */
export function PersonalSection() {
  return (
    <div className="space-y-8">
      <ProfileFrame />
      <ConnectedAccountsFrame />
      <EmailAddressesFrame />
      <ActiveDevicesFrame />
    </div>
  );
}

/** Photo + name, the Clerk profile your teammates see across the dashboard. */
function ProfileFrame() {
  const { user } = useUser();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setFirstName(user?.firstName ?? "");
    setLastName(user?.lastName ?? "");
  }, [user?.firstName, user?.lastName]);

  if (!user) {
    return (
      <Frame>
        <FrameHeader>
          <FrameTitle>Profile</FrameTitle>
        </FrameHeader>
        <FramePanel className="p-3">
          <Skeleton className="h-28 w-full rounded-lg" />
        </FramePanel>
      </Frame>
    );
  }

  const nameChanged =
    firstName.trim() !== (user.firstName ?? "") ||
    lastName.trim() !== (user.lastName ?? "");

  const handleSaveName = async () => {
    if (!nameChanged || savingName) return;
    setSavingName(true);
    try {
      await user.update({ firstName: firstName.trim(), lastName: lastName.trim() });
      toast.success("Name updated");
    } catch (error) {
      toastClerkError(error, "Failed to update your name");
    } finally {
      setSavingName(false);
    }
  };

  const handleSetAvatar = async (file: File | null) => {
    if (file && file.size > AVATAR_MAX_BYTES) {
      toast.error("The photo must be 10 MB or smaller");
      return;
    }
    setAvatarBusy(true);
    try {
      await user.setProfileImage({ file });
      toast.success(file ? "Photo updated" : "Photo removed");
    } catch (error) {
      toastClerkError(error, "Failed to update your photo");
    } finally {
      setAvatarBusy(false);
    }
  };

  const nameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") void handleSaveName();
  };

  return (
    <Frame>
      <FrameHeader>
        <FrameTitle>Profile</FrameTitle>
      </FrameHeader>
      <FramePanel className="flex flex-col gap-5">
        <div className="flex items-center gap-4">
          <Avatar variant="square" className="size-14 rounded-lg">
            <AvatarImage src={user.imageUrl} alt={user.fullName ?? "Your photo"} />
            <AvatarFallback>
              <User2Icon className="size-5 text-muted-foreground" />
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1">
              <Button
                variant="secondary"
                size="sm"
                disabled={avatarBusy}
                onClick={() => fileInputRef.current?.click()}
              >
                {avatarBusy ? <Spinner /> : <UploadIcon />}
                {user.hasImage ? "Replace photo" : "Upload photo"}
              </Button>
              {user.hasImage && (
                <Button
                  variant="destructive-ghost"
                  size="sm"
                  disabled={avatarBusy}
                  onClick={() => void handleSetAvatar(null)}
                >
                  <Trash2Icon />
                  Remove
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              PNG, JPG, GIF or WEBP, up to 10 MB.
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={AVATAR_TYPES}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) void handleSetAvatar(file);
            }}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="profile_first_name">First name</Label>
            <Input
              id="profile_first_name"
              value={firstName}
              disabled={savingName}
              onChange={(e) => setFirstName(e.target.value)}
              onKeyDown={nameKeyDown}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="profile_last_name">Last name</Label>
            <Input
              id="profile_last_name"
              value={lastName}
              disabled={savingName}
              onChange={(e) => setLastName(e.target.value)}
              onKeyDown={nameKeyDown}
            />
          </div>
        </div>
      </FramePanel>
      {nameChanged && (
        <FrameFooter className="flex justify-end">
          <Button
            onClick={() => void handleSaveName()}
            disabled={savingName}
            size="sm"
            className="shrink-0 animate-in fade-in-0 slide-in-from-left-2 duration-200"
          >
            {savingName ? <Spinner className="size-4" /> : <Save className="size-4" />}
            Save
          </Button>
        </FrameFooter>
      )}
    </Frame>
  );
}
